/**
 * useBranding Hook
 *
 * Access tenant branding. Fetches the public presentation endpoint on every
 * mount (login, password reset, invitation, mobile join) so a white-label
 * customer's first screen is branded even with an empty cache. Persisted
 * localStorage is only a speed-up — never the sole source. After sign-in the
 * authenticated `/tenants/branding` call fills in support contacts.
 */

import { useEffect } from 'react';
import { useBrandingStore } from '@/stores/brandingStore';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { fetchPublicBranding } from '@/api/publicBranding';
import { mergeBrandingWithDefaults, type TenantBrandingResponse } from '@/config/branding';

async function fetchAuthenticatedBranding(): Promise<TenantBrandingResponse> {
  return api.get<TenantBrandingResponse>('/tenants/branding');
}

/**
 * Hook for accessing tenant branding
 *
 * Features:
 * - Fetches public branding before authentication
 * - Refreshes authenticated branding (incl. support) after sign-in
 * - Caches in localStorage as a speed-up
 * - Provides convenient accessors with fallbacks
 *
 * @example
 * ```tsx
 * const { companyName, logo, colors, isLoading } = useBranding();
 *
 * return (
 *   <div>
 *     {logo ? <img src={logo.url} /> : <h1>{companyName}</h1>}
 *   </div>
 * );
 * ```
 */
export function useBranding() {
  const branding = useBrandingStore((s) => s.branding);
  const isLoading = useBrandingStore((s) => s.isLoading);
  const error = useBrandingStore((s) => s.error);
  const setBranding = useBrandingStore((s) => s.setBranding);
  const setLoading = useBrandingStore((s) => s.setLoading);
  const setError = useBrandingStore((s) => s.setError);

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const tenantName = useAuthStore((s) => s.user?.tenantName);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);

      if (isAuthenticated && tenantName) {
        try {
          const data = await fetchAuthenticatedBranding();
          if (cancelled) return;
          setBranding(data, tenantName);
          return;
        } catch (err: unknown) {
          // Fall through to the public endpoint; a cached look is still usable.
          if (cancelled) return;
          console.error('Failed to fetch authenticated branding:', err);
        }
      }

      try {
        const data = await fetchPublicBranding();
        if (cancelled) return;
        setBranding(data, data.tenantName);
      } catch (err: unknown) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load branding';
        console.error('Failed to fetch branding:', err);
        setError(message);
        // Keep whatever the store already has — cache is the fallback, not a
        // dummy row that would wipe a good persisted brand.
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
    // Re-run when auth changes so login can swap public → authenticated.
    // Persisted branding is shown immediately; this effect always revalidates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, tenantName]);

  // Merge branding with defaults for convenient access
  const merged = mergeBrandingWithDefaults(branding?.branding);

  return {
    // Raw branding data
    branding: branding?.branding,
    tenantName: branding?.tenantName,
    tenantSlug: branding?.tenantSlug,

    // Convenient accessors with fallbacks
    companyName: merged.companyName,
    logo: merged.logo,
    favicon: merged.favicon,
    colors: merged.colors,
    typography: merged.typography,

    // Support / help links
    support: branding?.support ?? null,

    // State
    isLoading,
    error,
  };
}
